import React, { useState, useEffect, useRef } from 'react';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Role } from '@/types/Role';
import { formatCurrency } from '@/utils/formatters';
import { isPercentageMethod, HALF_DAY_METHOD_OPTIONS, buildHalfDayHelpVars } from '@/utils/calculateHalfDayRate';
import { HelpButton } from '@/components/ui/help-button';
import { InlineHelpProvider, useInlineHelp } from '@/components/ui/inline-help-provider';
import { sectionHeader, sectionUnderline, fieldLabelClass, fieldSublabelClass, FieldLabel, billingBadgeBase, billingBadgeVariant, SegmentedToggle } from './roleFormStyles';
import { useRoleForm } from './useRoleForm';
import { PrefillHint } from './PrefillHint';
import { useScrollFadeMask } from '@/hooks/useScrollFade';
import billingStructureMd from './helpsheets/billing_structure.md?raw';
import halfDayBillingMd from './helpsheets/half_day_billing.md?raw';
import halfDayRateMethodMd from './helpsheets/half_day_rate_method.md?raw';

/** Header that fades out when an inline help sheet is open (no layout shift) */
function RoleSheetHeader({
  name, description, billingStructure, rate, headerHalfDayRate, mode,
}: {
  name: string;
  description: string;
  billingStructure: 'day_rate' | 'hourly' | null;
  rate: number | null;
  headerHalfDayRate: string | null;
  mode: 'create' | 'edit';
}) {
  const helpCtx = useInlineHelp();
  const isHelpOpen = helpCtx?.isHelpOpen ?? false;

  return (
    <SheetHeader className={cn("text-left px-1 pb-0 transition-opacity duration-150", isHelpOpen && "opacity-0 pointer-events-none")}>
      {!name ? (
        <>
          <SheetTitle className="heading-label text-lg">
            {mode === 'create' ? 'Add Role' : 'Edit Role'}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {mode === 'create' ? 'Configure a new role with name, billing structure, and rate' : 'Edit role details'}
          </SheetDescription>
        </>
      ) : (
        <>
          <SheetTitle asChild>
            <div className="flex items-center justify-between gap-3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300">
              <div className="min-w-0 flex-1">
                <div className="font-heading text-lg uppercase tracking-wide truncate text-foreground transition-all duration-200">
                  {name}
                </div>
                <p className={cn(
                  "text-xs text-muted-foreground truncate font-normal mt-0.5",
                  !description && "italic"
                )}>
                  {description || 'No description'}
                </p>
              </div>
              {billingStructure && (
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {rate ? (
                    <span className="font-mono text-sm font-bold tabular-nums text-primary-text transition-all duration-150">
                      {formatCurrency(rate, { showCents: false })}
                      <span className="text-[10px] sm:text-[12px] font-normal opacity-60">
                        /{billingStructure === 'day_rate' ? 'day' : 'hr'}
                      </span>
                    </span>
                  ) : null}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={cn(billingBadgeBase, billingBadgeVariant(billingStructure), 'transition-colors duration-200')}>
                      {billingStructure === 'day_rate' ? 'Day Rate' : 'Hourly'}
                    </span>
                    {headerHalfDayRate && (
                      <span className={cn(billingBadgeBase, billingBadgeVariant('day_rate'), 'whitespace-nowrap motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300')}>
                        &frac12; {headerHalfDayRate}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </SheetTitle>
          <SheetDescription className="sr-only">
            {mode === 'create' ? 'Configure a new role' : 'Edit role details'}
          </SheetDescription>
        </>
      )}
    </SheetHeader>
  );
}

/** Footer that auto-hides when an inline help sheet is open */
function RoleSheetFooter({
  mode, liveHint, hintShake, isFormValid,
  onSave, onDisabledClick, onDelete, onClose,
}: {
  mode: 'create' | 'edit';
  liveHint: string | null;
  hintShake: boolean;
  isFormValid: boolean;
  onSave: () => void;
  onDisabledClick: () => void;
  onDelete?: () => void;
  onClose: () => void;
}) {
  const helpCtx = useInlineHelp();
  const isHelpOpen = helpCtx?.isHelpOpen ?? false;

  return (
    <SheetFooter className={cn("mt-0 flex-col gap-2 px-1 pt-3 border-t border-border/50 bg-background transition-opacity duration-150", isHelpOpen && "opacity-0 pointer-events-none")}>
      {liveHint && (
        <p className={cn(
          'text-xs text-center transition-colors',
          hintShake ? 'text-destructive motion-safe:animate-[shake_0.4s_ease-in-out]' : 'text-muted-foreground'
        )}>{liveHint}</p>
      )}
      <div className="flex flex-row gap-2 w-full items-center">
        {mode === 'edit' && onDelete ? (
          <button
            type="button"
            className="flex items-center justify-center w-11 h-11 text-muted-foreground hover:text-destructive active:translate-y-px transition-colors"
            onClick={onDelete}
          >
            <Trash2 className="h-5 w-5" />
          </button>
        ) : mode === 'create' ? (
          <button
            type="button"
            className="flex items-center justify-center w-11 h-11 text-muted-foreground hover:text-foreground active:translate-y-px transition-colors"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        ) : null}
        <Button
          className={cn('flex-1 bg-primary text-primary-foreground font-semibold active:translate-y-px', !isFormValid && 'opacity-50')}
          onClick={isFormValid ? onSave : onDisabledClick}
          type="button"
        >
          {mode === 'create' ? 'Add Role' : 'Save'}
        </Button>
      </div>
    </SheetFooter>
  );
}

interface RoleDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  role?: Role | null;
  roles: Role[];
  lastSavedRole?: Role | null;
  onSave: (role: Role, originalRole?: Role) => void;
  onDelete?: (role: Role) => void;
}

export const RoleDetailSheet: React.FC<RoleDetailSheetProps> = ({
  open,
  onOpenChange,
  mode,
  role,
  roles,
  lastSavedRole,
  onSave,
  onDelete,
}) => {
  const {
    name, setName, description, setDescription,
    billingStructure, setBillingStructure, rate, setRate,
    allowHalfDay, setAllowHalfDay, halfDayMethod, setHalfDayMethod,
    halfDayMultiplier, setHalfDayMultiplier,
    halfDayFixedRate, setHalfDayFixedRate,
    halfDayFixedDiscount, setHalfDayFixedDiscount,
    errors, setErrors, showSuggestions, setShowSuggestions,
    prefillRoleName, clearPrefill,
    nameInputRef, suggestionsRef, descRef,
    filteredSuggestions, calculatedHalfDayRate, headerHalfDayRate,
    selectSuggestion, ensureHalfDayDefaults, validate, buildRole,
    populateFromRole, resetToDefaults, liveHint, isFormValid,
  } = useRoleForm({ roles, isEdit: mode === 'edit', originalRole: role, lastSavedRole });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollFadeRef = useScrollFadeMask(scrollContainerRef);

  /** Scroll the sheet to the bottom during a collapse animation using rAF tracking. */
  const scrollToBottom = () => {
    const container = scrollContainerRef.current;
    if (container) {
      let active = true;
      const track = () => {
        if (!active) return;
        container.scrollTop = container.scrollHeight;
        requestAnimationFrame(track);
      };
      requestAnimationFrame(track);
      setTimeout(() => { active = false; }, 300);
    }
  };

  // Reset form when sheet opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && role) populateFromRole(role);
      else resetToDefaults();
    }
  }, [open, mode, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const [hintShake, setHintShake] = useState(false);
  const triggerShake = () => {
    setHintShake(true);
    setTimeout(() => setHintShake(false), 600);
  };

  const handleSave = () => {
    if (!validate()) { triggerShake(); return; }
    onSave(buildRole(), mode === 'edit' && role ? role : undefined);
    onOpenChange(false);
  };

  const handleDisabledClick = () => {
    if (isFormValid) return;
    triggerShake();
    if (!name.trim()) nameInputRef.current?.focus();
  };

  const handleClose = () => onOpenChange(false);

  const handleDelete = () => {
    if (role && onDelete) {
      onDelete(role);
      onOpenChange(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        onClose={handleClose}
        className="max-h-[85vh] flex flex-col rounded-t pb-[max(1.25rem,env(safe-area-inset-bottom))]"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => {
            nameInputRef.current?.focus();
          }, 50);
        }}
      >
        <InlineHelpProvider>
        <RoleSheetHeader
          name={name}
          description={description}
          billingStructure={billingStructure}
          rate={rate}
          headerHalfDayRate={headerHalfDayRate}
          mode={mode}
        />

        <div ref={scrollFadeRef} className="flex-1 overflow-y-auto overflow-x-hidden px-2 pt-1 pb-3 scroll-fade-y">
        <div className="space-y-3">
          {/* ── Name & Description ── */}
          <div className="space-y-3">
            {/* Name */}
            <div className="space-y-2 relative">
              <FieldLabel label="Name" sublabel="Short code or title that clients recognize on invoices" />
              <Input
                ref={nameInputRef}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (mode === 'create') setShowSuggestions(true);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
                }}
                onFocus={() => { if (mode === 'create') setShowSuggestions(true); }}
                placeholder="e.g. A1, Stage Manager"
                autoComplete="off"
                className={cn('input-recessed', errors.name && 'border-destructive')}
              />

              {/* Suggestions dropdown */}
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute z-50 left-0 right-0 top-full mt-1 max-h-32 overflow-y-auto rounded border bg-popover text-popover-foreground shadow-md"
                >
                  {filteredSuggestions.map((s) => (
                    <button
                      key={s.name}
                      type="button"
                      disabled={s.isUsed}
                      className={cn(
                        'w-full px-3 py-3 text-left text-sm transition-colors',
                        s.isUsed ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent active:bg-accent'
                      )}
                      onMouseDown={s.isUsed ? undefined : (e) => {
                        e.preventDefault();
                        selectSuggestion(s);
                      }}
                    >
                      <span className="font-medium">{s.name}</span>
                      {s.isUsed ? (
                        <span className="ml-2 text-muted-foreground text-xs italic">already added</span>
                      ) : (
                        <span className="ml-2 text-muted-foreground text-xs line-clamp-1">
                          {s.description.slice(0, 80)}...
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <FieldLabel label="Description" sublabel="Scope of work, responsibilities, or skills you bring to this role" />
              <Textarea
                ref={descRef}
                value={description}
                maxLength={532}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Role description (optional)"
                rows={2}
                className="resize-none overflow-auto input-recessed"
              />
            </div>
          </div>

          {/* ── Section: Billing ── */}
          <div className="space-y-2">
            <div className={cn(sectionHeader, sectionUnderline)}>Billing</div>

            {/* Structure toggle */}
            <div className="space-y-2">
              <div>
                <div className="flex items-center gap-2">
                  <label className={cn(fieldLabelClass)}>Structure</label>
                  <HelpButton fieldName="Billing Structure" content={billingStructureMd} />
                </div>
                <p className={fieldSublabelClass}>Bill this role by the day or by the hour</p>
              </div>
              <SegmentedToggle
                options={[
                  { value: 'day_rate' as const, label: 'Day Rate' },
                  { value: 'hourly' as const, label: 'Hourly' },
                ]}
                value={billingStructure}
                onChange={(val) => {
                  setBillingStructure(val);
                  if (val === 'hourly') setAllowHalfDay(false);
                  if (errors.billingStructure)
                    setErrors((prev) => ({ ...prev, billingStructure: '' }));
                  // Auto-scroll to keep the newly revealed rate field visible
                  scrollToBottom();
                }}
              />
            </div>

            {/* Rate */}
            <AnimatedCollapse open={!!billingStructure}>
              <div className="space-y-1 pt-1">
                <div className="flex items-center justify-between gap-4">
                  <FieldLabel
                    label={billingStructure === 'day_rate' ? 'Day Rate' : 'Hourly Rate'}
                    sublabel={billingStructure === 'day_rate' ? 'Your base full-day fee' : 'Your base per-hour fee — used for overtime multiplier calculations'}
                    className="flex-1 min-w-0"
                  />
                  <NumberInput
                    unit="$"
                    unitPosition="left"
                    decimals={0}
                    value={rate ?? ''}
                    onChange={(e) => {
                      const val = e.target.value ? parseFloat(e.target.value) : null;
                      setRate(val);
                      clearPrefill();
                      if (errors.rate) setErrors((prev) => ({ ...prev, rate: '' }));
                    }}
                    min={0}
                    step={1}
                    placeholder="0"
                    className={cn('input-recessed', errors.rate && 'border-destructive')}
                  />
                </div>
                <PrefillHint roleName={prefillRoleName} />
              </div>
            </AnimatedCollapse>

            {/* Half-Day Billing (day_rate only) */}
            <AnimatedCollapse open={billingStructure === 'day_rate'}>
              <div className={cn("space-y-3", allowHalfDay && "border-t border-dashed border-border/70 pt-2 mt-1")}>
                {/* Toggle */}
                <div className="flex items-center justify-between gap-4 pt-1">
                  <div>
                    <div className="flex items-center gap-2">
                      <label className={cn(fieldLabelClass)}>Half-Day Billing</label>
                      <HelpButton fieldName="Half-Day Billing" content={halfDayBillingMd} />
                    </div>
                    <p className={fieldSublabelClass}>Offer a reduced rate for shorter shifts below the half-day threshold</p>
                  </div>
                  <Switch
                    checked={allowHalfDay}
                    onCheckedChange={(checked) => {
                      setAllowHalfDay(checked);
                      if (checked) ensureHalfDayDefaults();
                      if (checked) {
                        // Track scroll during collapse animation using rAF
                        scrollToBottom();
                      }
                    }}
                  />
                </div>

                {/* Half-day details — scroll reveals on open, animates out on close */}
                <AnimatedCollapse open={allowHalfDay}>
                  <div className="pt-1">
                    {/* Method selection — grid */}
                    <div className="space-y-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <label className={cn(fieldLabelClass)}>Method</label>
                          <HelpButton
                            fieldName="Half-Day Rate Method"
                            content={halfDayRateMethodMd}
                            variables={buildHalfDayHelpVars(rate || 500)}
                          />
                        </div>
                        <p className={fieldSublabelClass}>How the half-day rate is calculated from your full day rate</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {HALF_DAY_METHOD_OPTIONS.map(({ value, label, description }) => (
                          <button
                            key={value}
                            type="button"
                            className={cn(
                              'flex flex-col items-center justify-center rounded border py-3 px-2 cursor-pointer transition-colors text-center',
                              halfDayMethod === value
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-muted-foreground/30',
                            )}
                            onClick={() => {
                              const newMethod = value;
                              if (
                                isPercentageMethod(halfDayMethod) &&
                                isPercentageMethod(newMethod) &&
                                halfDayMethod !== newMethod
                              ) {
                                setHalfDayMultiplier(null);
                              }
                              setHalfDayMethod(newMethod);
                            }}
                          >
                            <span className={cn('text-sm font-medium', halfDayMethod === value ? 'text-primary-text' : 'text-foreground')}>{label}</span>
                            <span className={cn('text-xs font-mono', halfDayMethod === value ? 'text-primary-text' : 'text-muted-foreground')}>
                              {description}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Parameter input — keyed fade between methods */}
                    {halfDayMethod && (
                      <div key={isPercentageMethod(halfDayMethod) ? 'pct' : 'dollar'} className="space-y-1 pt-3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div key={halfDayMethod} className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300">
                              <FieldLabel
                                label={
                                  halfDayMethod === 'multiplier' ? 'Rate'
                                  : halfDayMethod === 'pct_discount' ? 'Discount'
                                  : halfDayMethod === 'fixed' ? 'Half-Day Rate'
                                  : 'Discount Amount'
                                }
                                sublabel={
                                  halfDayMethod === 'multiplier' ? 'Percentage of your full day rate charged for half days'
                                  : halfDayMethod === 'pct_discount' ? 'Percentage taken off your full day rate'
                                  : halfDayMethod === 'fixed' ? 'Fixed dollar amount you charge for a half-day shift'
                                  : 'Dollar amount subtracted from your full day rate for half days'
                                }
                              />
                            </div>
                          </div>
                          {isPercentageMethod(halfDayMethod) ? (
                            <NumberInput
                              unit="%"
                              unitPosition="right"
                              value={halfDayMultiplier ? Math.round(halfDayMultiplier * 100) : ''}
                              onChange={(e) => {
                                const pct = e.target.value ? parseFloat(e.target.value) : null;
                                setHalfDayMultiplier(pct !== null ? pct / 100 : null);
                                if (errors.halfDayMultiplier)
                                  setErrors((prev) => ({ ...prev, halfDayMultiplier: '' }));
                              }}
                              min={1}
                              max={99}
                              step={1}
                              placeholder={halfDayMethod === 'pct_discount' ? '25' : '60'}
                              className={cn(
                                'ml-auto input-recessed',
                                errors.halfDayMultiplier && 'border-destructive',
                              )}
                            />
                          ) : (
                            <NumberInput
                              key={halfDayMethod === 'fixed_discount' ? 'disc-input' : 'fixed-input'}
                              unit="$"
                              unitPosition="left"
                              decimals={0}
                              value={halfDayMethod === 'fixed_discount' ? (halfDayFixedDiscount ?? '') : (halfDayFixedRate ?? '')}
                              onChange={(e) => {
                                const val = e.target.value ? parseFloat(e.target.value) : null;
                                if (halfDayMethod === 'fixed_discount') {
                                  setHalfDayFixedDiscount(val);
                                  if (errors.halfDayFixedDiscount)
                                    setErrors((prev) => ({ ...prev, halfDayFixedDiscount: '' }));
                                } else {
                                  setHalfDayFixedRate(val);
                                  if (errors.halfDayFixedRate)
                                    setErrors((prev) => ({ ...prev, halfDayFixedRate: '' }));
                                }
                              }}
                              min={0}
                              step={1}
                              placeholder="0.00"
                              className={cn('input-recessed',
                                (halfDayMethod === 'fixed_discount' ? errors.halfDayFixedDiscount : errors.halfDayFixedRate) && 'border-destructive'
                              )}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Calculated preview */}
                    {halfDayMethod !== 'fixed' && calculatedHalfDayRate && (
                      <div className="bg-primary/5 border border-primary/10 rounded p-2 px-3 flex justify-between items-center mt-3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300">
                        <span className="font-label text-[10px] sm:text-[12px] uppercase text-muted-foreground">
                          Calculated Half-Day Rate
                        </span>
                        <span className="font-mono text-sm font-semibold text-primary-text">
                          {calculatedHalfDayRate}
                        </span>
                      </div>
                    )}
                  </div>
                </AnimatedCollapse>
              </div>
            </AnimatedCollapse>
          </div>
        </div>
        </div>

        <RoleSheetFooter
          mode={mode}
          liveHint={liveHint}
          hintShake={hintShake}
          isFormValid={isFormValid}
          onSave={handleSave}
          onDisabledClick={handleDisabledClick}
          onDelete={onDelete ? handleDelete : undefined}
          onClose={handleClose}
        />
        </InlineHelpProvider>
      </SheetContent>
    </Sheet>
  );
};
